'use client';

/**
 * Dashboard Data Hooks – TanStack Query edition
 *
 * Replaces the custom SWR-style caching (Phase 3 migration) with
 * TanStack Query for automatic caching, deduplication, refetch-on-focus,
 * and stale-while-revalidate behaviour.
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queries/queryKeys';
import { apiFetch } from '@/lib/api/client';
import type { PendingItemsResponse } from '@/app/api/dashboard/pending/route';
import type { HistoryItem, HistoryResponse } from '@/app/api/dashboard/history/route';

/**
 * Hook for pending items (tests, surveys, in-progress courses)
 * Supports loading more items per category with offset-based pagination
 */
export function usePendingItems(userId: string | undefined) {
  const queryClient = useQueryClient();

  // Accumulated items per category
  const [allTests, setAllTests] = useState<PendingItemsResponse['pendingTests']>([]);
  const [allSurveys, setAllSurveys] = useState<PendingItemsResponse['pendingSurveys']>([]);
  const [allCourses, setAllCourses] = useState<PendingItemsResponse['inProgressCourses']>([]);

  // Pagination state per category
  const [testsOffset, setTestsOffset] = useState(0);
  const [surveysOffset, setSurveysOffset] = useState(0);
  const [coursesOffset, setCoursesOffset] = useState(0);

  // Totals and hasMore flags
  const [testsTotal, setTestsTotal] = useState(0);
  const [surveysTotal, setSurveysTotal] = useState(0);
  const [coursesTotal, setCoursesTotal] = useState(0);
  const [hasMoreTests, setHasMoreTests] = useState(false);
  const [hasMoreSurveys, setHasMoreSurveys] = useState(false);
  const [hasMoreCourses, setHasMoreCourses] = useState(false);

  // Loading more state per category
  const [loadingMoreTests, setLoadingMoreTests] = useState(false);
  const [loadingMoreSurveys, setLoadingMoreSurveys] = useState(false);
  const [loadingMoreCourses, setLoadingMoreCourses] = useState(false);

  // Track previous userId to detect changes
  const prevUserIdRef = useRef(userId);

  // Reset state when userId changes
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      setAllTests([]);
      setAllSurveys([]);
      setAllCourses([]);
      setTestsOffset(0);
      setSurveysOffset(0);
      setCoursesOffset(0);
      setTestsTotal(0);
      setSurveysTotal(0);
      setCoursesTotal(0);
      setHasMoreTests(false);
      setHasMoreSurveys(false);
      setHasMoreCourses(false);
      prevUserIdRef.current = userId;
    }
  }, [userId]);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<PendingItemsResponse>({
    queryKey: queryKeys.dashboard.pending(userId ?? ''),
    queryFn: () => apiFetch<PendingItemsResponse>('/api/dashboard/pending'),
    enabled: !!userId,
    staleTime: 30_000,
  });

  // Sync initial page data into local state whenever TanStack Query delivers fresh data
  useEffect(() => {
    if (data) {
      setAllTests(data.pendingTests);
      setAllSurveys(data.pendingSurveys);
      setAllCourses(data.inProgressCourses);
      setTestsOffset(data.pendingTests.length);
      setSurveysOffset(data.pendingSurveys.length);
      setCoursesOffset(data.inProgressCourses.length);
      setTestsTotal(data.testsTotal);
      setSurveysTotal(data.surveysTotal);
      setCoursesTotal(data.coursesTotal);
      setHasMoreTests(data.hasMoreTests);
      setHasMoreSurveys(data.hasMoreSurveys);
      setHasMoreCourses(data.hasMoreCourses);
    }
  }, [data]);

  // Load more functions per category with session refresh retry
  const loadMoreTests = useCallback(async () => {
    if (!hasMoreTests || loadingMoreTests) return;

    setLoadingMoreTests(true);
    try {
      const moreData = await apiFetch<PendingItemsResponse>(
        `/api/dashboard/pending?testsOffset=${testsOffset}`
      );
      setAllTests(prev => [...prev, ...moreData.pendingTests]);
      setTestsOffset(prev => prev + moreData.pendingTests.length);
      setHasMoreTests(moreData.hasMoreTests);
    } catch (err) {
      console.error('Error loading more tests:', err);
      // On persistent auth failure, stop showing load more
      setHasMoreTests(false);
    } finally {
      setLoadingMoreTests(false);
    }
  }, [testsOffset, hasMoreTests, loadingMoreTests]);

  const loadMoreSurveys = useCallback(async () => {
    if (!hasMoreSurveys || loadingMoreSurveys) return;

    setLoadingMoreSurveys(true);
    try {
      const moreData = await apiFetch<PendingItemsResponse>(
        `/api/dashboard/pending?surveysOffset=${surveysOffset}`
      );
      setAllSurveys(prev => [...prev, ...moreData.pendingSurveys]);
      setSurveysOffset(prev => prev + moreData.pendingSurveys.length);
      setHasMoreSurveys(moreData.hasMoreSurveys);
    } catch (err) {
      console.error('Error loading more surveys:', err);
      setHasMoreSurveys(false);
    } finally {
      setLoadingMoreSurveys(false);
    }
  }, [surveysOffset, hasMoreSurveys, loadingMoreSurveys]);

  const loadMoreCourses = useCallback(async () => {
    if (!hasMoreCourses || loadingMoreCourses) return;

    setLoadingMoreCourses(true);
    try {
      const moreData = await apiFetch<PendingItemsResponse>(
        `/api/dashboard/pending?coursesOffset=${coursesOffset}`
      );
      setAllCourses(prev => [...prev, ...moreData.inProgressCourses]);
      setCoursesOffset(prev => prev + moreData.inProgressCourses.length);
      setHasMoreCourses(moreData.hasMoreCourses);
    } catch (err) {
      console.error('Error loading more courses:', err);
      setHasMoreCourses(false);
    } finally {
      setLoadingMoreCourses(false);
    }
  }, [coursesOffset, hasMoreCourses, loadingMoreCourses]);

  // Optimistic mutation helper (matches old API)
  const mutate = useCallback((newData: PendingItemsResponse | ((prev: PendingItemsResponse | null) => PendingItemsResponse)) => {
    if (!userId) return;
    const key = queryKeys.dashboard.pending(userId);
    queryClient.setQueryData<PendingItemsResponse>(key, (old) => {
      if (typeof newData === 'function') {
        return newData(old ?? null);
      }
      return newData;
    });
  }, [userId, queryClient]);

  // Invalidate – triggers background refetch
  const invalidate = useCallback(() => {
    if (!userId) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.pending(userId) });
  }, [userId, queryClient]);

  // Build enhanced data object with accumulated items
  const enhancedData: PendingItemsResponse | null = data ? {
    ...data,
    pendingTests: allTests,
    pendingSurveys: allSurveys,
    inProgressCourses: allCourses,
    testsTotal,
    surveysTotal,
    coursesTotal,
    hasMoreTests,
    hasMoreSurveys,
    hasMoreCourses,
  } : null;

  return {
    data: enhancedData,
    isLoading,
    isStale: false, // TanStack Query handles staleness internally
    error: error as Error | null,
    refetch: async () => { await refetch(); },
    mutate,
    invalidate,
    // Load more functions
    loadMoreTests,
    loadMoreSurveys,
    loadMoreCourses,
    // Loading more states
    loadingMoreTests,
    loadingMoreSurveys,
    loadingMoreCourses,
    // Has more flags
    hasMoreTests,
    hasMoreSurveys,
    hasMoreCourses,
  };
}

/**
 * Hook for history with cursor pagination
 */
export function useHistory(userId: string | undefined) {
  const queryClient = useQueryClient();
  const [allItems, setAllItems] = useState<HistoryItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Track previous userId to detect changes
  const prevUserIdRef = useRef(userId);

  // Reset state when userId changes (logout, user switch, or cache clear)
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      setAllItems([]);
      setCursor(null);
      setHasMore(false);
      setTotalCount(0);
      setIsLoadingMore(false);
      prevUserIdRef.current = userId;
    }
  }, [userId]);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<HistoryResponse>({
    queryKey: queryKeys.dashboard.history(userId ?? ''),
    queryFn: () => apiFetch<HistoryResponse>('/api/dashboard/history'),
    enabled: !!userId,
    staleTime: 60_000,
  });

  // Sync initial page data into local state whenever TanStack Query delivers fresh data
  useEffect(() => {
    if (data) {
      setAllItems(data.items);
      setCursor(data.nextCursor);
      setHasMore(data.hasMore);
      setTotalCount(data.totalCount);
    }
  }, [data]);

  // Load more function with session refresh retry
  const loadMore = useCallback(async () => {
    if (!cursor || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const moreData = await apiFetch<HistoryResponse>(
        `/api/dashboard/history?cursor=${encodeURIComponent(cursor)}`
      );
      setAllItems(prev => [...prev, ...moreData.items]);
      setCursor(moreData.nextCursor);
      setHasMore(moreData.hasMore);
    } catch (err) {
      console.error('Error loading more history:', err);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [cursor, isLoadingMore, hasMore]);

  // Optimistic mutation helper
  const mutate = useCallback((newData: HistoryResponse | ((prev: HistoryResponse | null) => HistoryResponse)) => {
    if (!userId) return;
    const key = queryKeys.dashboard.history(userId);
    queryClient.setQueryData<HistoryResponse>(key, (old) => {
      if (typeof newData === 'function') {
        return newData(old ?? null);
      }
      return newData;
    });
  }, [userId, queryClient]);

  // Invalidate – triggers background refetch
  const invalidate = useCallback(() => {
    if (!userId) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.history(userId) });
  }, [userId, queryClient]);

  return {
    data,
    isLoading,
    isStale: false,
    error: error as Error | null,
    refetch: async () => { await refetch(); },
    mutate,
    invalidate,
    items: allItems,
    hasMore,
    totalCount,
    isLoadingMore,
    loadMore,
  };
}

/**
 * Hook for dashboard cache invalidation utilities.
 * Must be used inside a component (needs QueryClient from context).
 */
export function useDashboardCache() {
  const queryClient = useQueryClient();

  return {
    /** Invalidate pending items cache (call when user completes test/survey) */
    invalidatePending: (userId: string) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.pending(userId) });
    },

    /** Invalidate history cache (call when new completion is recorded) */
    invalidateHistory: (userId: string) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.history(userId) });
    },

    /** Invalidate all dashboard caches for user */
    invalidateAll: (userId: string) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all(userId) });
    },

    /** Optimistically update pending items count */
    decrementPendingCount: (userId: string, itemType: 'test' | 'survey' | 'course') => {
      const key = queryKeys.dashboard.pending(userId);
      queryClient.setQueryData<PendingItemsResponse>(key, (old) => {
        if (!old) return old;
        const updated = { ...old };
        if (itemType === 'test' && updated.pendingTests.length > 0) {
          updated.pendingTests = updated.pendingTests.slice(1);
          updated.totalCount = Math.max(0, updated.totalCount - 1);
        } else if (itemType === 'survey' && updated.pendingSurveys.length > 0) {
          updated.pendingSurveys = updated.pendingSurveys.slice(1);
          updated.totalCount = Math.max(0, updated.totalCount - 1);
        } else if (itemType === 'course' && updated.inProgressCourses.length > 0) {
          updated.inProgressCourses = updated.inProgressCourses.slice(1);
          updated.totalCount = Math.max(0, updated.totalCount - 1);
        }
        return updated;
      });
    },

    /** Prefetch dashboard data (call on hover over dashboard link) */
    prefetch: async (userId: string) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.dashboard.pending(userId),
        queryFn: () => apiFetch<PendingItemsResponse>('/api/dashboard/pending'),
        staleTime: 30_000,
      });
      queryClient.prefetchQuery({
        queryKey: queryKeys.dashboard.history(userId),
        queryFn: () => apiFetch<HistoryResponse>('/api/dashboard/history'),
        staleTime: 60_000,
      });
    },
  };
}

/**
 * Legacy static export for backward compatibility in non-component contexts.
 * For use in components, prefer the `useDashboardCache()` hook instead.
 *
 */
